import SearchIcon from '@mui/icons-material/Search';
import {
    Button,
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { Hybrid } from '../hybrids/hybrid-card';

/** The name column carries the radio, so it takes the wider half, as the design does. */
const NAME_COLUMN_WIDTH = '50%';

/** The header stays visible while the rows scroll, so it needs its own opaque background. */
const headCellSx = {
    px: 1,
    py: 1,
    backgroundColor: (theme: Theme) => theme.palette.background.paper,
};

/**
 * "Buscá un híbrido para comparar" — picks one more hybrid to add to the density comparison.
 * Only hybrids that aren't already selected are listed.
 */
function AddHybridModal({
    open,
    onClose,
    onAdd,
    candidates,
}: {
    open: boolean;
    onClose: () => void;
    onAdd: (id: number) => void;
    candidates: Hybrid[];
}) {
    const units = useUnitProvider();
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return candidates;
        return candidates.filter((hybrid) => hybrid.catalogName?.toLowerCase().includes(term));
    }, [candidates, search]);

    const close = () => {
        setSearch('');
        setSelectedId(undefined);
        onClose();
    };

    return (
        <ModalV2
            open={open}
            onClose={close}
            title={_('Buscá un híbrido para comparar')}
            maxWidth='sm'
            actions={
                <Stack direction='row' justifyContent='flex-end' width='100%'>
                    <Button
                        variant='contained'
                        color='secondary'
                        disabled={selectedId === undefined}
                        onClick={() => {
                            if (selectedId !== undefined) onAdd(selectedId);
                            close();
                        }}
                        data-testid='add-hybrid-confirm'
                    >
                        {_('Agregar')}
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={2} width='100%'>
                <TextField
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={_('Busca por nombre de híbrido')}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'primary.mui.inactive' }} /> }}
                    fullWidth
                />

                <TableContainer sx={{ maxHeight: 360 }}>
                    <Table stickyHeader size='small' sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...headCellSx, width: NAME_COLUMN_WIDTH }}>
                                    <Typography variant='caption' fontWeight={700} color='primary'>
                                        {_('Híbridos recomendados')}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={headCellSx}>
                                    <Typography variant='caption' fontWeight={700} color='primary'>
                                        {_('Rendimiento estimado')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} sx={{ border: 0 }}>
                                        <Typography variant='body2' color='primary.mui.inactive' p={2}>
                                            {_('No encontramos híbridos con ese nombre.')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((hybrid) => {
                                const estimated = hybrid.recommended?.yield
                                    ? units.toYield(hybrid.recommended.yield, hybrid.brand?.type || 'corn', 0)
                                    : undefined;
                                return (
                                    <TableRow
                                        key={hybrid.id}
                                        hover
                                        selected={selectedId === hybrid.id}
                                        onClick={() => setSelectedId(hybrid.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ px: 1, py: 0, width: NAME_COLUMN_WIDTH }}>
                                            <Stack direction='row' alignItems='center'>
                                                <Radio
                                                    checked={selectedId === hybrid.id}
                                                    color='secondary'
                                                    inputProps={{ 'aria-label': hybrid.catalogName }}
                                                />
                                                <Typography variant='body2' color='primary'>
                                                    {hybrid.catalogName}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ px: 1 }}>
                                            <Typography variant='body2' color='primary'>
                                                {estimated ? `${estimated.localeValue} ${estimated.unit}` : '-'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        </ModalV2>
    );
}

export default AddHybridModal;
